(() => {
  const selector = 'mb-studio-carousel, mb-photo-carousel, slider-component, slideshow-component';
  const attached = new WeakMap();

  function attach(host) {
    if (attached.has(host)) return;
    const controller = new AbortController();
    attached.set(host, controller);
    const options = { signal: controller.signal };
    const custom = host.matches('mb-studio-carousel, mb-photo-carousel');
    let gesture = null;
    let suppressUntil = 0;

    function start(x, y, kind) {
      gesture = { x, y, kind, horizontal: false };
      host.stop?.();
    }

    function move(x, y, event) {
      if (!gesture) return;
      const dx = Math.abs(x - gesture.x);
      const dy = Math.abs(y - gesture.y);
      if (!gesture.horizontal && dy > 12 && dy > dx) {
        gesture = null;
        host.restart?.();
        return;
      }
      if (!gesture.horizontal && dx > 12 && dx > dy * 1.15) gesture.horizontal = true;
      if (gesture.horizontal && event.cancelable) event.preventDefault();
    }

    function finish(x, y) {
      if (!gesture) return;
      const { horizontal, kind } = gesture;
      const dx = x - gesture.x;
      const dy = y - gesture.y;
      gesture = null;
      const threshold = Math.max(32, Math.min(64, host.clientWidth * .075));
      if (horizontal && Math.abs(dx) >= threshold && Math.abs(dx) > Math.abs(dy) * 1.15) {
        suppressUntil = Date.now() + 600;
        if (custom && typeof host.show === 'function') {
          const dots = Array.from(host.querySelectorAll('[data-slide]'));
          const current = dots.findIndex(dot => dot.getAttribute('aria-pressed') === 'true');
          if (dots.length > 1) host.show(Math.max(current, 0) + (dx < 0 ? 1 : -1));
        } else {
          const slider = host.querySelector('[id^="Slider-"]');
          const item = slider?.querySelector('[id^="Slide-"]');
          if (slider && item) slider.scrollBy({
            left: (dx < 0 ? 1 : -1) * item.getBoundingClientRect().width,
            behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
          });
        }
      }
      if (kind === 'touch') host.restart?.();
    }

    if (custom) {
      host.addEventListener('touchstart', event => {
        if (event.touches.length !== 1 || event.target.closest('button, input, select, textarea, video, model-viewer')) return;
        const touch = event.touches[0];
        start(touch.clientX, touch.clientY, 'touch');
      }, { ...options, passive: true });
      host.addEventListener('touchmove', event => {
        if (!gesture || gesture.kind !== 'touch' || event.touches.length !== 1) return;
        const touch = event.touches[0];
        move(touch.clientX, touch.clientY, event);
      }, { ...options, passive: false });
      host.addEventListener('touchend', event => {
        if (!gesture || gesture.kind !== 'touch') return;
        const touch = event.changedTouches[0];
        if (touch) finish(touch.clientX, touch.clientY);
      }, options);
      host.addEventListener('touchcancel', () => { gesture = null; host.restart?.(); }, options);
    }

    host.addEventListener('mousedown', event => {
      if (event.button !== 0 || event.target.closest('button, input, select, textarea, video, model-viewer')) return;
      start(event.clientX, event.clientY, 'mouse');
    }, options);
    window.addEventListener('mousemove', event => {
      if (gesture?.kind === 'mouse') move(event.clientX, event.clientY, event);
    }, { ...options, passive: false });
    window.addEventListener('mouseup', event => {
      if (gesture?.kind === 'mouse') finish(event.clientX, event.clientY);
    }, options);
    host.addEventListener('dragstart', event => {
      if (event.target.closest('img, a')) event.preventDefault();
    }, options);
    host.addEventListener('click', event => {
      if (Date.now() < suppressUntil) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, { ...options, capture: true });
  }

  function initialize(root) {
    if (root.matches?.(selector)) attach(root);
    root.querySelectorAll(selector).forEach(attach);
  }

  initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    const hosts = Array.from(event.target.querySelectorAll(selector));
    if (event.target.matches(selector)) hosts.push(event.target);
    hosts.forEach(host => { attached.get(host)?.abort(); attached.delete(host); });
  });
})();
